import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { getJudgingDataForCategory } from 'services/vistaJudging';
import { getAIJudgment } from 'services/qualificationEvaluatorService';
import { Submission, JudgingData, Judgment } from 'types/evaluation';
// Sub-components
import EvaluatorHeader from './EvaluatorHeader';
import MaterialsViewer from './MaterialsViewer';
import ScoringSection from './ScoringSection';
import NavigationPanel from './NavigationPanel';
import { useRoleInfo } from 'hooks/useRoleInfo';

interface QualificationEvaluatorProps {
  submission: Submission;
  onEvaluationSubmit: (data: any) => Promise<void>;
  isLastSubmission?: boolean;
  existingJudgment?: Judgment | null;
  onBackToOverview: () => void;
  allSubmissions?: Submission[];
  currentSubmissionIndex: number;
  onNavigateToSubmission: (index: number) => void;
  isRefreshing?: boolean;
  year: number;
  isReadOnly?: boolean;
}

const QualificationEvaluator: React.FC<QualificationEvaluatorProps> = ({
  submission,
  onEvaluationSubmit,
  existingJudgment,
  onBackToOverview,
  allSubmissions = [],
  currentSubmissionIndex,
  onNavigateToSubmission,
  year,
  isReadOnly = false,
}) => {
  const { userIsSuperAdmin } = useRoleInfo();
  const { profile } = useAuth(); // Get judge information from auth context
  const [scores, setScores] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [activeTab, setActiveTab] = useState('info');
  const [loadingStates, setLoadingStates] = useState({
    presentation: true,
    video: true,
  });

  // New state for rubric data
  const [rubricData, setRubricData] = useState<JudgingData | null>(null);
  const [loadingRubric, setLoadingRubric] = useState(true);
  const [rubricError, setRubricError] = useState<string | null>(null);

  // State for master toggle - controls all guides at once
  const [allGuidesExpanded, setAllGuidesExpanded] = useState(true);

  // State for collapsible Key Points section
  const [keyPointsExpanded, setKeyPointsExpanded] = useState(true);

  // AI Judge suggested scores
  const [aiJudgment, setAiJudgment] = useState<Judgment | null>(null);

  useEffect(() => {
    const fetchAIJudgment = async () => {
      if (!submission?.documentId) {
        setAiJudgment(null);
        return;
      }
      const result = await getAIJudgment(submission.documentId);
      setAiJudgment(result);
    };
    fetchAIJudgment();
  }, [submission?.documentId]);

  // Check if this is an existing evaluation being revised
  const isRevision = !!existingJudgment;
  const isDraft = existingJudgment?.overall_summary === 'Draft';
  const isCompleted =
    existingJudgment?.overall_summary === 'Completed' ||
    existingJudgment?.overall_summary === 'Completed Final';

  // Fetch rubric data when submission changes
  useEffect(() => {
    const fetchRubricData = async () => {
      if (!submission?.category) {
        console.warn('⚠️ No category found in submission:', submission);
        setLoadingRubric(false);
        setRubricData(null);
        setRubricError(null);
        setScores({});
        return;
      }

      try {
        // Clear previous state immediately when starting new fetch
        setLoadingRubric(true);
        setRubricError(null);
        setRubricData(null);
        setScores({});

        const judgingData = await getJudgingDataForCategory(
          submission.category,
          year
        );

        if (
          !judgingData ||
          !judgingData.aspects ||
          judgingData.aspects.length === 0
        ) {
          throw new Error(
            `No rubric aspects found for category: ${submission.category}`
          );
        }

        setRubricData(judgingData);

        // Initialize scores object with aspect IDs
        const initialScores: Record<string, string> = {};
        judgingData.aspects.forEach(aspect => {
          if (aspect.documentId) {
            initialScores[aspect.documentId] = '';
          }
        });

        // If this is a revision, populate existing scores
        if (existingJudgment && existingJudgment.aspect_scores) {
          existingJudgment.aspect_scores.forEach(aspectScore => {
            initialScores[aspectScore.aspect_id] = aspectScore.value.toString();
          });
        }

        setScores(initialScores);
      } catch (error: any) {
        console.error(
          '❌ Error fetching rubric data for category:',
          submission.category,
          error
        );
        setRubricError(error.message);

        // Fallback to hardcoded criteria if rubric fetch fails
        const fallbackScores: Record<string, string> = {
          criteria1: '',
          criteria2: '',
          criteria3: '',
        };

        // If this is a revision with existing judgment, try to populate fallback scores
        if (existingJudgment && existingJudgment.aspect_scores) {
          existingJudgment.aspect_scores.forEach((aspectScore, index) => {
            const criteriaKey = `criteria${index + 1}`;
            if (fallbackScores.hasOwnProperty(criteriaKey)) {
              fallbackScores[criteriaKey] = aspectScore.value.toString();
            }
          });
        }

        setScores(fallbackScores);
        console.log('⚠️ Using fallback criteria due to rubric fetch error');
      } finally {
        setLoadingRubric(false);
      }
    };

    fetchRubricData();
  }, [submission?.category, submission?.documentId, existingJudgment]); // Added existingJudgment to populate scores on revision

  // Handle iframe load events
  const handleIframeLoad = () => {
    setLoadingStates(prev => ({ ...prev, presentation: false }));
  };

  const handleVideoLoad = () => {
    setLoadingStates(prev => ({ ...prev, video: false }));
  };

  // Reset loading states when switching tabs
  useEffect(() => {
    if (activeTab === 'presentation') {
      setLoadingStates(prev => ({ ...prev, presentation: true }));
    } else if (activeTab === 'video') {
      setLoadingStates(prev => ({ ...prev, video: true }));
    }
  }, [activeTab]);

  // Reset UI state when submission changes (for navigation)
  useEffect(() => {
    setActiveTab('info'); // Always start with info tab
    setLoadingStates({ presentation: true, video: true }); // Reset loading states
    setErrors({}); // Clear any validation errors
    // Keep scoring guides open by default - don't reset them
    setKeyPointsExpanded(true); // Reset key points expansion
  }, [submission?.documentId]); // Trigger when submission changes

  // Keyboard navigation support
  useEffect(() => {
    const handleKeyPress = (event: KeyboardEvent) => {
      // Only handle keyboard navigation if we have navigation props
      if (
        !onNavigateToSubmission ||
        !allSubmissions ||
        allSubmissions.length <= 1
      )
        return;

      // Check if user is typing in an input field
      const target = event.target as HTMLElement;
      if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA') return;

      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        onNavigateToSubmission(currentSubmissionIndex - 1);
      } else if (event.key === 'ArrowRight') {
        event.preventDefault();
        onNavigateToSubmission(currentSubmissionIndex + 1);
      }
    };

    document.addEventListener('keydown', handleKeyPress);
    return () => document.removeEventListener('keydown', handleKeyPress);
  }, [onNavigateToSubmission, allSubmissions, currentSubmissionIndex]);

  // Handle score input changes
  const handleScoreChange = (aspectId: string, value: string) => {
    setScores(prev => ({
      ...prev,
      [aspectId]: value,
    }));

    // Clear error when user starts typing
    if (errors[aspectId]) {
      setErrors(prev => ({
        ...prev,
        [aspectId]: '',
      }));
    }
  };

  // Validate and submit evaluation
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validate all scores are provided and between 0-10
    const newErrors: Record<string, string> = {};
    const aspectsToValidate = rubricData?.aspects || [];

    if (aspectsToValidate.length === 0) {
      // Fallback validation for hardcoded criteria
      ['criteria1', 'criteria2', 'criteria3'].forEach(criteria => {
        if (scores[criteria] === '' || scores[criteria] === undefined) {
          newErrors[criteria] = 'Score is required';
        } else {
          const numScore = parseInt(scores[criteria]);
          if (isNaN(numScore) || numScore < 0 || numScore > 10) {
            newErrors[criteria] = 'Score must be between 0 and 10';
          }
        }
      });
    } else {
      // Validate dynamic aspects
      aspectsToValidate.forEach(aspect => {
        const aspectId = aspect.documentId || '';
        const score = scores[aspectId];

        if (score === '' || score === undefined) {
          newErrors[aspectId] = 'Score is required';
        } else {
          const numScore = parseInt(score);
          if (isNaN(numScore) || numScore < 0 || numScore > 10) {
            newErrors[aspectId] = 'Score must be between 0 and 10';
          }
        }
      });
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setIsSubmitting(true);
    setErrors({});

    try {
      // Prepare evaluation data with proper score mapping
      let evaluationScores: Record<string, number> = {};

      if (rubricData?.aspects && rubricData.aspects.length > 0) {
        // Use dynamic rubric aspects
        rubricData.aspects.forEach((aspect, index) => {
          // Map to the expected criteria format for the service
          const criteriaKey = `criteria${index + 1}`;
          const aspectId = aspect.documentId || '';
          evaluationScores[criteriaKey] = parseInt(scores[aspectId]);
        });
      } else {
        // Fallback to hardcoded criteria
        evaluationScores = {
          criteria1: parseInt(scores.criteria1),
          criteria2: parseInt(scores.criteria2),
          criteria3: parseInt(scores.criteria3),
        };
      }

      const evaluationData = {
        submissionId: submission.documentId || submission.id,
        scores: evaluationScores,
        evaluatedAt: new Date().toISOString(),
        evaluatorId: profile?.email || 'unknown-judge',
        evaluatorName: profile?.fullName || 'Unknown Judge',
        isRevision: isRevision,
        existingJudgmentId: existingJudgment?.documentId,
        // Note: rubricData is fetched by the service, not passed from component
      };

      await onEvaluationSubmit(evaluationData);

      // Don't reset scores after submission to prevent flicker
      // Scores will be updated when existingJudgment prop changes
      console.log(
        '✅ Evaluation submitted, keeping current scores to prevent flicker'
      );
    } catch (error: any) {
      console.error('❌ Error in QualificationEvaluator submission:', {
        error: error.message,
        stack: error.stack,
        evaluationData: {
          submissionId: submission?.documentId || submission?.id,
          scores,
          evaluatorId: profile?.email,
          evaluatorName: profile?.fullName,
        },
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="space-y-8">
      <EvaluatorHeader
        onBackToOverview={onBackToOverview}
        isRevision={isRevision}
        isDraft={isDraft}
      />

      <MaterialsViewer
        submission={submission}
        activeTab={activeTab}
        setActiveTab={setActiveTab}
        keyPointsExpanded={keyPointsExpanded}
        setKeyPointsExpanded={setKeyPointsExpanded}
        loadingStates={loadingStates}
        handleIframeLoad={handleIframeLoad}
        handleVideoLoad={handleVideoLoad}
      />

      <ScoringSection
        submission={submission}
        scores={scores}
        errors={errors}
        handleScoreChange={handleScoreChange}
        handleSubmit={handleSubmit}
        isSubmitting={isSubmitting}
        isRevision={isRevision}
        isDraft={isDraft}
        loadingRubric={loadingRubric}
        rubricData={rubricData}
        rubricError={rubricError}
        allGuidesExpanded={allGuidesExpanded}
        setAllGuidesExpanded={setAllGuidesExpanded}
        isReadOnly={!userIsSuperAdmin && (isReadOnly || isCompleted)}
        aiJudgment={aiJudgment}
      />

      <NavigationPanel
        allSubmissions={allSubmissions}
        currentSubmissionIndex={currentSubmissionIndex}
        onNavigateToSubmission={onNavigateToSubmission}
      />
    </div>
  );
};

export default QualificationEvaluator;
